/**
 * Pure helpers for the session "raw timeline" view — extracted from
 * DeviceSessionTimeline.tsx so they can be unit-tested without dragging the
 * page's React/Leaflet import graph into the node test env (see
 * sessionAggregates.ts for the Overview-tab counterparts).
 *
 * Most session-scoped events are emitted *on exit* and carry a dwell that
 * describes the preceding window — `view_visit` fires when the user leaves a
 * view, `product_view` fires when focus leaves the card, etc. Anchoring rows
 * to the dwell's start (not its emission) flips the table from "leaving X at
 * T" to "started X at T-dwell", which matches the operator's mental model.
 */

import type { EventRow } from '../api/hooks';

export interface RawRow {
  ev: EventRow;
  start_ms: number;
  end_ms: number;
  duration_ms: number;
  depth: number;
}

// Dwell window (ms) used to anchor a row at the start of its activity.
export function getDwellMs(ev: EventRow): number {
  const d = (ev.data ?? {}) as Record<string, unknown>;
  switch (ev.event_type) {
    case 'view_visit':
    case 'product_view':
    case 'feature_view':
    case 'gallery_view':
    case 'compare_view':
      return Number(d.dwell_ms ?? 0);
    case 'content_play':
      return Number(d.watched_ms ?? 0);
    case 'source_dwell':
      // DE-168: dwell_s measures the *previous* source, not this event's own
      // window — the switch itself is an instant. Return 0 so buildRawRows
      // anchors the row at its ts (a point event) and it sorts at the moment
      // of the switch, not at the start of the prior source's (often
      // whole-session) dwell, which wrongly hoisted it to the top of the list.
      return 0;
    default:
      return 0;
  }
}

// Window-containment nesting: events whose dwell window is enclosed by
// another's are shown indented under it. One pass with a stack — events
// sorted by start_ms ASC and (on ties) longer windows first so the
// container is processed before its children. Point events (duration 0)
// can be children of a window but cannot themselves be parents.
export function buildRawRows(events: EventRow[], sessionStartMs: number): RawRow[] {
  const rows: RawRow[] = events.map((ev) => {
    const tsMs = new Date(ev.ts).getTime();
    const dwell = getDwellMs(ev);
    return {
      ev,
      start_ms: tsMs - dwell,
      end_ms: tsMs,
      duration_ms: dwell,
      depth: 0,
    };
  });
  rows.sort((a, b) => {
    if (a.start_ms !== b.start_ms) return a.start_ms - b.start_ms;
    return b.duration_ms - a.duration_ms;
  });
  const stack: RawRow[] = [];
  for (const r of rows) {
    while (stack.length > 0 && stack[stack.length - 1]!.end_ms < r.end_ms) {
      stack.pop();
    }
    r.depth = stack.length;
    if (r.duration_ms > 0) stack.push(r);
  }
  // Belt-and-braces: snap negative offsets (e.g. an anchor-style event
  // that predates the session start ULID by a tick) to 0 so the table
  // doesn't display "+-0:01".
  for (const r of rows) {
    if (r.start_ms < sessionStartMs) r.start_ms = sessionStartMs;
  }
  return rows;
}
